import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const navLinks = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          scrolled
            ? "bg-[#0A0F2C]/85 backdrop-blur-xl border-b border-white/10 shadow-[0_8px_28px_rgba(10,15,44,0.45)]"
            : "bg-transparent"
        }`}
      >
        <div
          className={`container-x flex items-center justify-between transition-[padding] duration-500 ease-out ${
            scrolled ? "py-2.5" : "py-4"
          }`}
        >
          <Link
            to="/"
            className="flex items-center"
            data-testid="logo-link"
          >
            <img
              src="/digital-archdeacon-logo.png"
              alt="Digital Archdeacon Media Ltd"
              className={`w-auto transition-[height] duration-500 ease-out ${
                scrolled ? "h-9" : "h-12"
              }`}
              style={{ filter: "drop-shadow(0 0 12px rgba(30,111,255,0.35))" }}
            />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors ${
                    isActive ? "text-white" : "text-[#CBD5E1] hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link
              to="/request-quote"
              data-testid="nav-cta-quote"
              className={`glow-btn inline-flex items-center justify-center rounded-full bg-electric font-semibold text-white transition-all duration-500 ease-out ${
                scrolled ? "px-4 py-2 text-[13px]" : "px-5 py-3 text-sm"
              }`}
            >
              Request a Free Quote
            </Link>
          </div>

          <button
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(true)}
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5 text-white"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Mobile slide-from-right drawer */}
      <div
        aria-hidden={!open}
        data-testid="mobile-drawer-overlay"
        onClick={() => setOpen(false)}
        className={`md:hidden fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="mobile-drawer"
        className={`md:hidden fixed top-0 right-0 z-[70] h-full w-[88%] max-w-sm bg-[#0A0F2C] border-l border-white/10 shadow-[0_0_60px_rgba(30,111,255,0.25)] transition-transform duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <Link
            to="/"
            data-testid="mobile-logo-link"
          >
            <img
              src="/digital-archdeacon-logo.png"
              alt="Digital Archdeacon Media Ltd"
              className="h-10 w-auto"
            />
          </Link>
          <button
            aria-label="Close menu"
            data-testid="mobile-menu-close"
            onClick={() => setOpen(false)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5 text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="p-6 flex flex-col gap-1">
          {navLinks.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block px-4 py-4 rounded-xl text-lg font-heading font-semibold transition-all ${
                  isActive
                    ? "bg-electric/15 text-white border border-electric/30"
                    : "text-[#CBD5E1] hover:bg-white/5 hover:text-white border border-transparent"
                }`
              }
              style={{ transitionDelay: open ? `${80 + i * 40}ms` : "0ms" }}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/request-quote"
            data-testid="mobile-nav-cta-quote"
            className="mt-4 inline-flex items-center justify-center rounded-full bg-electric px-5 py-4 text-sm font-semibold text-white"
          >
            Request a Free Quote
          </Link>
        </nav>
        <div className="mt-auto p-6 border-t border-white/10">
          <p className="text-xs uppercase tracking-widest text-[#CBD5E1]">Get in touch</p>
          <a href="tel:+2348147276648" className="mt-2 block text-white font-medium">+234-814-727-6648</a>
          <a href="mailto:info@digitalarchdeacon.com" className="text-electric text-sm mt-1 block">info@digitalarchdeacon.com</a>
        </div>
      </aside>
    </>
  );
}
